import { useState, useEffect, useRef } from 'react';
import { X, Send, User, Package, Loader2, Plus, Image as ImageIcon, Video } from 'lucide-react';
import { chatAPI } from '../../../../config/chat-api';
import './ChatWindow.css';
import { useNavigate } from 'react-router-dom';
import ProductSelectionModal from '../../../Common/ProductSelectionModal/ProductSelectionModal';
import { toast } from 'react-toastify';
import { IS_ALFAJR } from '../../../../constants/general';

const ChatWindow = ({ chat, onClose, adminData, onChatUpdate }) => {
  const IMAGE_MAX_SIZE = 1 * 1024 * 1024;
  const VIDEO_MAX_SIZE = 6 * 1024 * 1024;

  const MAX_ATTACHMENTS = 6;
  const formatCompressionSize = (bytes) => bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;
  const formatDuration = (seconds) => `${Number(seconds || 0).toFixed(1)}s`;
  const logCompressionResult = (scope, file, finalFile, status, reason = '', durationSec = null) => {
    const savedPct = file.size > 0 ? ((1 - (finalFile.size / file.size)) * 100).toFixed(1) : '0.0';
    console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalFile.size)} | saved: ${savedPct}% | duration: ${durationSec !== null ? formatDuration(durationSec) : 'n/a'} | status: ${status}${reason ? ` | reason: ${reason}` : ''} | file: ${file.name}`);
  };
  const logCompressionSkip = (scope, file, reason) => {
    console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(file.size)} | saved: 0.0% | duration: n/a | status: original kept | reason: ${reason} | file: ${file.name}`);
  };
  const [messages, setMessages] = useState([]);
  const [chatDetails, setChatDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [mediaMenuOpen, setMediaMenuOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [compressingMedia, setCompressingMedia] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [composerError, setComposerError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const ffmpegRef = useRef(null);
  const cancelCompressionRef = useRef(false);
  const navigate = useNavigate();
  const messagesCountRef = useRef(0);
  
  const isVersionAbove = (currentVersion, targetVersion) => {
    if (!currentVersion) return false;
    const v1 = currentVersion.split('.').map(Number);
    const v2 = targetVersion.split('.').map(Number);
    for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
      const p1 = v1[i] || 0;
      const p2 = v2[i] || 0;
      if (p1 > p2) return true;
      if (p1 < p2) return false;
    }
    return false;
  };

  const showAdvancedFeatures = chatDetails?.appVersion && isVersionAbove(chatDetails.appVersion, "2.0.6");

  useEffect(() => {
    if (chat?._id) {
      fetchChatDetails(chat._id);
    }
  }, [chat?._id]);

  useEffect(() => {
    setClosing(false);
    setSending(false);
    setSelectedMedia([]);
    setSelectedProductIds([]);
    setComposerError('');
    setMediaMenuOpen(false);
  }, [chat?._id]);

  useEffect(() => {
    return () => {
      try {
        ffmpegRef.current?.terminate?.();
      } catch { }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!chat?._id) return;

    const interval = setInterval(async () => {
      try {
        const response = await chatAPI.getChatById(chat._id);
        if (response.success && response.data) {
          const newMessages = response.data.messages || [];
          const newStatus = response.data.status;
          // Check if status changed or messages changed
          if (newStatus !== chatDetails?.status || newMessages.length > messagesCountRef.current) {
            
            // If new user messages arrived, play alert
            if (newMessages.length > messagesCountRef.current) {
                const addedMessages = newMessages.slice(messagesCountRef.current);
                const hasNewUserMessage = addedMessages.some(m => m.isUser === true);
                if (hasNewUserMessage) {
                  const audio = new Audio('/alerts/new_msg_alert.mp3');
                  audio.play().catch(() => { });
                }
            }

            setChatDetails(response.data);
            setMessages(newMessages);
            messagesCountRef.current = newMessages.length;
            if (onChatUpdate) onChatUpdate();
          }
        }
      } catch (err) {
        console.error('Chat polling error:', err);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [chat?._id, chatDetails?.status]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatDetails = async (chatId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatAPI.getChatById(chatId);
      if (response.success) {
        setChatDetails(response.data);
        const msgs = response.data.messages || [];
        setMessages(msgs);
        messagesCountRef.current = msgs.length;
      } else {
        setError(response.error || 'Failed to load chat');
      }
    } catch (err) {
      setError('Error loading chat details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const text = messageText.trim();
    if ((!text && selectedMedia.length === 0 && selectedProductIds.length === 0) || isBusy || isClosed || compressingMedia) return;
    setComposerError('');

    setSending(true);
    const mediaPreviews = selectedMedia.map((file) => ({ file, url: URL.createObjectURL(file) }));
    const optimisticAttachments = mediaPreviews.map(({ file, url }) => ({
      type: file.type?.startsWith('video/') ? 'video' : 'image',
      url
    }));

    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      text,
      isUser: false,
      time: 'Just now',
      isRead: false,
      adminName: 'Ablemart',
      attachments: optimisticAttachments,
      products: selectedProductIds
    };
    setMessages(prev => {
      const updated = [...prev, optimisticMsg];
      messagesCountRef.current = updated.length;
      return updated;
    });
    setMessageText('');
    const mediaToSend = [...selectedMedia];
    const productsToSend = [...selectedProductIds];
    setSelectedMedia([]);
    setSelectedProductIds([]);

    try {
      const response = await chatAPI.sendMessage(chat._id, text, mediaToSend, productsToSend);
      if (response.success) {
        if (response.data) {
          setMessages(prev =>
            prev.map(m => m.id === optimisticMsg.id ? { ...response.data } : m)
          );
        }
      } else {
        setMessages(prev => {
          const updated = prev.filter(m => m.id !== optimisticMsg.id);
          messagesCountRef.current = updated.length;
          return updated;
        });
        setMessageText(text);
        setSelectedMedia(mediaToSend);
        setSelectedProductIds(productsToSend);
      }
    } catch {
      setMessages(prev => {
        const updated = prev.filter(m => m.id !== optimisticMsg.id);
        messagesCountRef.current = updated.length;
        return updated;
      });
      setMessageText(text);
      setSelectedMedia(mediaToSend);
      setSelectedProductIds(productsToSend);
    } finally {
      mediaPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const compressImage = (file, targetKbRange) => {
    const sizeKB = file.size / 1024;
    if (sizeKB < 200) {
      logCompressionSkip('Chat Image', file, 'below 200 KB threshold');
      return Promise.resolve(file);
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          const maxDim = 1600;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let minQ = 0.35, maxQ = 0.92, q = 0.75, attempts = 0;
          const outType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp';
          const iterate = () => {
            canvas.toBlob((blob) => {
              attempts++;
              const kb_res = blob.size / 1024;
              if ((kb_res >= targetKbRange[0] && kb_res <= targetKbRange[1]) || attempts >= 5) {
                if (blob.size < file.size) {
                  resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + (outType === 'image/webp' ? '.webp' : '.jpg'), { type: outType, lastModified: Date.now() }));
                } else {
                  resolve(file);
                }
                return;
              }
              if (kb_res > targetKbRange[1]) maxQ = q; else minQ = q;
              q = (minQ + maxQ) / 2;
              iterate();
            }, outType, q);
          };
          iterate();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const loadFFmpegIfNeeded = async () => {
    if (ffmpegRef.current?.loaded) return;
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');
    ffmpegRef.current = new FFmpeg();
    let lastMilestone = 0;
    ffmpegRef.current.on('progress', ({ progress }) => {
      const pct = Math.floor(progress * 100);
      if (pct >= lastMilestone + 10) {
        lastMilestone = Math.floor(pct / 10) * 10;
        console.log(`[Chat Video] Compressing... ${lastMilestone}%`);
      }
    });
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpegRef.current.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
  };

  const getVideoDuration = (file) => new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration || 10);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(10);
    };
    video.src = URL.createObjectURL(file);
  });

  const compressVideo = async (videoFile) => {
    const startedAt = Date.now();
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = ffmpegRef.current;
    const input = `chat_in_${Date.now()}.mp4`;
    const output = `chat_out_${Date.now()}.mp4`;
    const duration = await getVideoDuration(videoFile);
    await ffmpeg.writeFile(input, await fetchFile(videoFile));

    const sizeMB = videoFile.size / (1024 * 1024);
    let targetRatio = 0.5; // Default 50% compression

    if (sizeMB > 5) {
      targetRatio = 0.3; // High compression for near 6MB (70% reduction)
    } else if (sizeMB > 2) {
      targetRatio = 0.4; // Medium-high compression (60% reduction)
    }

    const targetBytes = videoFile.size * targetRatio;
    const totalBitrateKbps = Math.floor(((targetBytes * 8) / duration) / 1000);
    const videoBitrateKbps = Math.max(300, totalBitrateKbps - 128);

    await ffmpeg.exec([
      '-i', input, '-c:v', 'libx264', '-preset', 'superfast', '-b:v', `${videoBitrateKbps}k`,
      '-maxrate', `${Math.floor(videoBitrateKbps * 1.5)}k`, '-bufsize', `${videoBitrateKbps * 2}k`,
      '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2', '-pix_fmt', 'yuv420p', '-movflags', 'faststart',
      '-c:a', 'aac', '-b:a', '128k', output
    ]);
    const data = await ffmpeg.readFile(output);
    await ffmpeg.deleteFile(input);
    await ffmpeg.deleteFile(output);
    const compressed = new Blob([data.buffer], { type: 'video/mp4' });
    const elapsedSec = ((Date.now() - startedAt) / 1000).toFixed(1);
    console.log(`[Chat Video] | original: ${formatCompressionSize(videoFile.size)} | final: ${formatCompressionSize(compressed.size)} | saved: ${((1 - compressed.size / videoFile.size) * 100).toFixed(1)}% | duration: ${elapsedSec}s | status: processed | file: ${videoFile.name}`);
    return compressed;
  };

  const handlePickMediaType = (type) => {
    if (isBusy || isClosed) return;
    setMediaMenuOpen(false);
    if (type === 'products') {
      setProductModalOpen(true);
      return;
    }
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    fileInputRef.current.accept = type === 'video' ? 'video/*' : 'image/*';
    fileInputRef.current.multiple = type === 'image';
    fileInputRef.current.click();
  };

  const handleMediaChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (selectedMedia.length + files.length > MAX_ATTACHMENTS) {
      toast.warn(`You can attach up to ${MAX_ATTACHMENTS} files`);
      return;
    }
    const processedFiles = [];

    setCompressingMedia(true);
    try {
      for (const file of files) {
        const isVideo = file.type.startsWith('video/');
        if (!isVideo && file.size > IMAGE_MAX_SIZE) {
          toast.error('Image exceeds the 1MB limit');
          continue;
        }
        if (isVideo && file.size > VIDEO_MAX_SIZE) {
          toast.error('Video exceeds the 6MB limit');
          continue;
        }

        let finalFile = file;
        try {
          cancelCompressionRef.current = false;
          if (isVideo) {
            const compressionStartedAt = Date.now();
            await loadFFmpegIfNeeded();
            console.log(`[Chat Video] Compressing: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
            const compressed = await compressVideo(file);
            const durationSec = (Date.now() - compressionStartedAt) / 1000;
            if (!cancelCompressionRef.current && compressed.size < file.size && compressed.size <= VIDEO_MAX_SIZE) {
              finalFile = new File([compressed], file.name.replace(/\.[^/.]+$/, '.mp4'), { type: 'video/mp4' });
              logCompressionResult('Chat Video', file, finalFile, 'compressed', '', durationSec);
            } else {
              const reason = compressed.size >= file.size
                ? 'output larger'
                : 'limit guard: compressed output exceeds max size';
              logCompressionResult('Chat Video', file, file, 'original kept', reason, durationSec);
            }
          } else {
            if (file.size >= 200 * 1024) {
              let targetRange;
              if (file.size >= 700 * 1024) {
                targetRange = [400, 500];
              } else if (file.size >= 300 * 1024) {
                targetRange = [200, 300];
              } else {
                targetRange = [200, 250];
              }
              console.log(`[Chat Image] Compressing: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
              const compressionStartedAt = Date.now();
              finalFile = await compressImage(file, targetRange);
              logCompressionResult(
                'Chat Image',
                file,
                finalFile,
                finalFile.size < file.size ? 'compressed' : 'original kept',
                finalFile.size < file.size ? '' : 'already optimized',
                (Date.now() - compressionStartedAt) / 1000
              );
            } else {
              finalFile = file;
              logCompressionSkip('Chat Image', file, 'limit guard: below 200 KB threshold');
            }

            if (finalFile.size > IMAGE_MAX_SIZE) {
              toast.error('Image exceeds the 1MB limit');
              continue;
            }
          }
        } catch (err) {
          finalFile = file;
        }
        processedFiles.push(finalFile);
      }
    } finally {
      if (!cancelCompressionRef.current) setCompressingMedia(false);
    }
    setSelectedMedia((prev) => [...prev, ...processedFiles]);
  };

  const handleReopenChat = async () => {
    if (closing) return;
    setClosing(true);
    try {
      const response = await chatAPI.updateChatStatus(chat._id, 'active');
      if (response.success) {
        setChatDetails(prev => ({ ...prev, status: 'active' }));
        if (onChatUpdate) onChatUpdate();
      }
    } finally {
      setClosing(false);
    }
  };

  const handleCloseChat = async () => {
    if (closing || chatDetails?.status === 'closed') return;
    setClosing(true);
    try {
      const response = await chatAPI.updateChatStatus(chat._id, 'closed');
      if (response.success) {
        setChatDetails(prev => ({ ...prev, status: 'closed' }));
        if (onChatUpdate) onChatUpdate();
      }
    } finally {
      setClosing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleUserClick = () => {
    if (chatDetails?.userPhone) {
      navigate('/dashboard', {
        state: {
          page: 'users',
          userPhone: chatDetails.userPhone
        }
      });
    }
  };

  const handleOrderClick = () => {
    if (chatDetails?.orderReference) {
      navigate('/dashboard', {
        state: {
          page: 'orders',
          orderReference: chatDetails.orderReference
        }
      });
    }
  };

  const isClosed = chatDetails?.status === 'closed';
  const isBusy = loading || sending || closing;

  return (
    <div className="chat-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chat-panel">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <span className="chat-header-title">Chat Details</span>
            <div className="chat-header-meta">
              {chatDetails?.userName && (
                <span
                  className="chat-meta-item"
                  onClick={chatDetails.userPhone && chatDetails.userName !== "Guest User" ? handleUserClick : undefined}
                  style={{
                    cursor: chatDetails.userPhone && chatDetails.userName !== "Guest User" ? 'pointer' : 'default',
                    textDecoration: chatDetails.userPhone && chatDetails.userName !== "Guest User" ? 'underline' : 'none'
                  }}
                  title={chatDetails.userPhone && chatDetails.userName !== "Guest User" ? "View User Details" : ""}
                >
                  <User size={13} />
                  {chatDetails.userName}
                </span>
              )}
              {chatDetails?.orderReference && (
                <span
                  className="chat-meta-item"
                  onClick={handleOrderClick}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  title="View Order Details"
                >
                  <Package size={13} />
                  Order #{chatDetails.orderReference}
                </span>
              )}
            </div>
          </div>
          <div className="chat-header-actions">
            {!isClosed && (
              <button
                className="chat-close-chat-btn"
                onClick={handleCloseChat}
                disabled={closing}
              >
                {closing ? <Loader2 size={14} className="chat-spin" /> : <X size={14} />}
                {closing ? 'Closing…' : 'Close Chat'}
              </button>
            )}
            {isClosed && (
              <>
                <span className="chat-closed-badge">Closed</span>
                <button
                  className="chat-reopen-btn"
                  onClick={handleReopenChat}
                  disabled={closing}
                >
                  {closing ? <Loader2 size={14} className="chat-spin" /> : null}
                  {closing ? 'Reopening…' : 'Reopen Chat'}
                </button>
              </>
            )}
            <button className="chat-dismiss-btn" onClick={onClose} title="Dismiss panel">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages">
          {loading ? (
            <div className="chat-state-center">
              <div className="chat-spinner" />
              <p>Loading messages…</p>
            </div>
          ) : error ? (
            <div className="chat-state-center chat-state-error">
              <p>{error}</p>
              <button onClick={() => fetchChatDetails(chat._id)} className="chat-retry-btn">
                Retry
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-state-center">
              <p>No messages yet</p>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`chat-message-row ${msg.isUser ? 'chat-row-user' : 'chat-row-admin'}`}
                >
                  <div className="chat-bubble-wrap">
                    <span className="chat-sender-name">
                      {msg.isUser ? (chatDetails?.userName || 'User') : 'AbleMart'}
                    </span>
                    <div className={`chat-bubble ${msg.isUser ? 'chat-bubble-user' : 'chat-bubble-admin'}`}>
                      {msg.attachments?.map((att, i) => (
                        att.type === 'image' ? (
                          <img
                            key={i}
                            src={att.url}
                            alt="attachment"
                            className="chat-attachment-img"
                            onClick={() => window.open(att.url, '_blank')}
                          />
                        ) : att.type === 'video' ? (
                          <video
                            key={i}
                            src={att.url}
                            className="chat-attachment-video"
                            controls
                            preload="metadata"
                          />
                        ) : null
                      ))}
                      {Array.isArray(msg.products) && msg.products.filter((product) => product && typeof product === 'object' && product.name).length > 0 && (
                        <div className="chat-products-grid">
                          {msg.products.filter((product) => product && typeof product === 'object' && product.name).map((product, pIdx) => (
                            <div key={pIdx} className="chat-product-card" onClick={() => navigate(`/dashboard`, { state: { page: 'products', search: product.name } })}>
                              {(product?.images?.[0]?.image || product.image) ? (
                                <img src={product?.images?.[0]?.image || product.image} alt={product.name} className="chat-product-thumb" />
                              ) : (
                                <div className="chat-product-thumb chat-product-thumb-empty">No image</div>
                              )}
                              <div className="chat-product-info">
                                <span className="chat-product-name">{product.name}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.text && <div className="chat-text-content">{msg.text}</div>}
                    </div>
                    <div className="chat-msg-meta">
                      <span className="chat-msg-time">{msg.time}</span>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className={`chat-input-area ${isClosed ? 'chat-input-disabled' : ''}`}>
          {isClosed ? (
            <p className="chat-closed-note">This chat has been closed.</p>
          ) : (
            <>
              {(selectedMedia.length > 0 || selectedProductIds.length > 0) && (
                <div className="chat-selected-files-bar">
                  <div className="chat-selected-files-summary">
                    {selectedMedia.filter((file) => file.type?.startsWith('image/')).length > 0 && (
                      <span className="chat-selected-chip">
                        <ImageIcon size={16} />
                        {selectedMedia.filter((file) => file.type?.startsWith('image/')).length}
                      </span>
                    )}
                    {selectedMedia.filter((file) => file.type?.startsWith('video/')).length > 0 && (
                      <span className="chat-selected-chip">
                        <Video size={16} />
                        {selectedMedia.filter((file) => file.type?.startsWith('video/')).length}
                      </span>
                    )}
                    {selectedProductIds.length > 0 && (
                      <span className="chat-selected-chip">
                        {selectedProductIds.length} product{selectedProductIds.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button
                    className="chat-selected-media-remove"
                    onClick={() => {
                      setSelectedMedia([]);
                      setSelectedProductIds([]);
                    }}
                    disabled={sending}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="chat-input-row">
              {!IS_ALFAJR && (
                <div className="chat-input-tools">
                  <button
                    className="chat-attach-btn"
                    onClick={() => setMediaMenuOpen(prev => !prev)}
                    disabled={isBusy || isClosed || compressingMedia}
                    title="Attach media"
                  >
                    {compressingMedia ? <Loader2 size={18} className="chat-spin" /> : <Plus size={18} />}
                  </button>
                  {mediaMenuOpen && (
                    <div className="chat-media-menu">
                      <button className="chat-media-menu-item" onClick={() => handlePickMediaType('image')}>Image</button>
                      {showAdvancedFeatures && (
                        <>
                          <button className="chat-media-menu-item" onClick={() => handlePickMediaType('video')}>Video</button>
                          <button className="chat-media-menu-item" onClick={() => handlePickMediaType('products')}>Products</button>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="chat-hidden-file-input"
                    onChange={handleMediaChange}
                  />
                </div>
              )}
              <textarea
                ref={inputRef}
                className="chat-textarea"
                placeholder="Type your message…"
                value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  if (composerError) setComposerError('');
                }}
                onKeyDown={handleKeyDown}
                rows={2}
              />
              <button
                className="chat-send-btn"
                onClick={handleSendMessage}
                disabled={(!messageText.trim() && selectedMedia.length === 0 && selectedProductIds.length === 0) || isBusy || compressingMedia}
                title="Send message"
              >
                {sending
                  ? <Loader2 size={18} className="chat-spin" />
                  : <Send size={18} />
                }
              </button>
              </div>
              {composerError && <p className="chat-composer-error">{composerError}</p>}
            </>
          )}
        </div>
      </div>
      <ProductSelectionModal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        selectedProductIds={selectedProductIds}
        onSave={(ids) => {
          setSelectedProductIds(ids);
          setProductModalOpen(false);
        }}
      />
    </div>
  );
};

export default ChatWindow;